import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, Avatar, Card, CardContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment'; 
import { getStatus } from '../helpers/utils';
import ConfiLogo from "../images/Confindustria_solo_logo.png";

const useStyles = makeStyles((theme) => ({
  main: {
    paddingRight: '20px',
    width:'100%',
    height: '100%', 
    display: 'flex',
    flexDirection: 'column',
  },  
  chip: {
    fontSize: '24px',
    borderRadius: '10px',
    backgroundColor: '#f0f0f0',
    marginRight: '4px',
    marginBottom: '4px',
    color: '#333',
    '& .MuiChip-label': {
      fontSize: '16px',
      fontFamily: "Raleway",
      fontWeight: "bold",
    },
  },
  highlightedChip: {
    backgroundColor: '#093F75',
    color: 'white',
  },
  statusDot: {
    width: '23px',
    height: '23px',
    borderRadius: '50%',
    marginLeft: '10px',
  },
  logoDivBigAvatar: {
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    objectFit:'contain',
    justifyContent: 'center',
    boxShadow: '0px 0px 26px #00000012',
    backgroundColor: 'white',
    width: '16.66vh',
    height: '16.66vh',
    '@media (max-width: 1700px)': {
      width: '15vh',
      height: '15vh',
    },
    '@media (max-width: 1500px)': {
      width: '13.5vh',
      height: '13.5vh',
    },
    '@media (max-width: 1200px)': {
      width: '12vh',
      height: '12vh',
    },
    '@media (max-width: 992px)': {
      width: '10vh',
      height: '10vh',
    },
    '@media (max-width: 768px)': {
      width: '8vh',
      height: '8vh',
    },
  },
  avatarImg: {
    objectFit: 'contain', // Ensures the image scales without being cropped
    width: '80%', // Set the width of the image
    height: 'auto', // Maintain aspect ratio
  },
  titleevent: {
    color: '#000000',
    fontWeight: 'bold',
    fontFamily: "Raleway",
    fontSize: '46px',
    marginTop:'46px',
    marginLeft : 20
  },
  dataEvent: {
    fontWeight: 'normal',
    fontSize: '20px',
    color: '#14121F',
    textTransform: 'uppercase',
    marginTop:'20px',
    '& span': {
      fontSize: 'inherit',
      fontFamily: 'inherit',
    },
  },
  status: {
    width: '50%',
    margin: '20px 0px 0px 20px',
    fontWeight: 'bold',
    fontSize: '20px',
    color: '#14121F',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  desc: {
    fontSize: '16px',
    lineHeight: '32px',
    minWidth:'100%',
    marginTop:'30px',
  },
  salebuttons: {
    display:'flex',
    flexDirection:'column',
    color: '#093F75',
    borderTopRightRadius: '20px',
    borderBottomRightRadius: '20px',
    fontWeight: 'bold',
    fontSize: '24px',
    backgroundColor: '#F5F5F5',
    padding: '20px',
  },
  chipssale: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 20,
    marginLeft: 2,
  },
  endContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    flexGrow: 1,
  }
}));

const EventContainer = ({ eventName, eventdatainizio, eventdatafine, eventStatus, eventDescription, eventLogo, labels, onLastLabelHighlight, onHighlightedIndexChange }) => {
  const classes = useStyles();
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    if (labels && labels.length > 0) {
      const interval = setInterval(() => {
        setHighlightedIndex(prevIndex => {
          const newIndex = (prevIndex + 1) % labels.length;
          if (newIndex === 0 && prevIndex === labels.length - 1) {
            onLastLabelHighlight();
          }
          onHighlightedIndexChange(newIndex);
          return newIndex;
        });
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [labels, onLastLabelHighlight, onHighlightedIndexChange]);

  const formatTime = (datetime) => <span>{moment(datetime).format('HH:mm')}</span>;

  const isValidDate = (datetime) => moment(datetime, moment.ISO_8601, true).isValid();

  const { text: statusText = '', color: statusDotColor = '' } = getStatus(eventdatainizio, eventdatafine) || {};

  return (
    <div className={classes.main}>
        <div style={{display:'flex', flexDirection:'row', minWidth: '400px', marginLeft : 20}}>
            <Avatar alt={eventName} src={eventLogo ?? ConfiLogo} className={classes.logoDivBigAvatar} imgProps={{ className: classes.avatarImg }}/>
        </div>
        <div className={classes.titleevent}>
            {eventName}
            {isValidDate(eventdatainizio) && isValidDate(eventdatafine) && (
                <div className={classes.dataEvent}>
                    {formatTime(eventdatainizio)} - {formatTime(eventdatafine)}
                </div>
            )}
        </div>
        {classes.status && <div className={classes.status}>
          {statusText}
          {classes.status && <div className={classes.statusDot} style={{ backgroundColor: statusDotColor }} />}
        </div>}
        <div className={classes.desc}>{eventDescription}</div>
        <div className={classes.endContainer}>
          <div className={classes.salebuttons}>
            INDICAZIONI PER
            <div className={classes.chipssale}>
              {labels.map((label, index) => (
                <Chip
                  key={index}
                  label={label}
                  className={`${classes.chip} ${index === highlightedIndex ? classes.highlightedChip : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

EventContainer.propTypes = {
  eventName: PropTypes.string.isRequired,
  eventdatainizio: PropTypes.string.isRequired,
  eventdatafine: PropTypes.string.isRequired,
  eventStatus: PropTypes.string,
  eventDescription: PropTypes.string.isRequired,
  eventLogo: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLastLabelHighlight: PropTypes.func.isRequired,
  onHighlightedIndexChange: PropTypes.func.isRequired,
};

export default EventContainer;
